import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useStore, StoreTypes } from 'context';
import { Roles, isTeacherRole } from 'constants/role';
import { AnnotationType } from 'constants/annotationTypes';
import { useSyncClassPreparationDispatcher } from 'customHooks/syncClassPreparation';
import styles from './index.module.scss';
import AnnotationWrapDialog from '../AnnotationWrapDialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import LinearProgress from '@material-ui/core/LinearProgress';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import EnhancedTable from '../../AnnotationActivityTab/list';
import { LottiePlayer, LottiePlayerReference } from 'components/LottieLoading/lottiePlayer';
import Icon from 'components/Icon';
import dateformat from 'dateformat';
import { makeStyles } from '@material-ui/core';
import { usePreparation } from 'customHooks/preparation';
import { useRefreshReader } from 'customHooks/reader';
import { useReadAnnotations, useCreateAnnotation } from 'customHooks/db';
import { ReaderEvent, ReaderToolsEvent } from 'events/EventTypes';
import { EventBus } from 'events/EventBus';
import { useFirebase } from 'customHooks/firebase';
import uuid from 'util/uuid';
import * as types from 'constants/actionTypes';
import { LOGIN_POPUP } from "constants/loginTypes";
import { useOnlineCheck } from 'customHooks/onlineCheck';
import { useGAEvent, getSmoothBrushState } from 'components/AppService/GAService';
import WarningDialog from '../WarningDialog';
import SwitchButton from 'components/common/SwitchButton';
import { getNaniOneClassCookie } from 'components/Login/Popup/login.js';
import { postToNative, NativeEvent } from 'util/native';
import Repository from 'repositories/Repository';

const useStyles = makeStyles({
  paper: {
    background: "#F2F4F8",
    borderRadius: "16px",
    width: "810px",
    maxWidth: "calc(100% - 32px)",
    margin: "16px 16px 52px 16px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    flex: "1 1 auto",
    minHeight: 0,
    boxShadow: '0 8px 16px 0 rgba(145, 158, 171, 0.16)',
    "@media(max-width:1024px)": {
      width: "550px",
      margin: "16px",
    },
    "& .MuiDialogTitle-root": {
      padding: "24px",
      position: "relative",
      "@media(max-width:500px)": {
        "&.MuiDialogTitle-root": {
          padding: "16px 20px",
        },
      },
      "@media(max-width:768px)": {
        "& .MuiTypography-root": {
          fontSize: "18px",
        },
      },
      "& .closeDialog": {
        position: "absolute",
        width: "14px",
        right: "32px",
        top: "50%",
        transform: "translateY(-50%)",
        cursor: "pointer",
      },
    },
    "& .MuiDialogContent-root": {
      padding: "0px 32px",
      overflowY: "scroll",
      minHeight: "90px",
      flex: "1 1 auto",
      "@media(max-width:500px)": {
        padding: "0 20px",
      },
      "&.noData": {
        "@media(max-width:900px)": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "stretch",
          overflow: "hidden",
        },
      },
    },
    "& .MuiDialogActions-root": {
      padding: "24px 32px",
      display: "flex",
      justifyContent: "space-between",
      "& div": {
        margin: "0",
      },
      "@media (max-height: 420px) and (orientation: landscape)": {
        padding: "8px 32px",
      },
      "@media (max-width: 500px)": {
        padding: "16px 20px",
        flexDirection: "column",
        alignItems: "flex-start",
      },
      "& .actionSyncBtn": {
        width: "5.75rem",
        padding: "8px 11px",
        whiteSpace: "nowrap",
        background: "#FFF",
        '&:hover' : {
          background: '#B3BBC3'
        }
      },
      "& .actionAddBtn": {
        width: "8.75rem",
        whiteSpace: "nowrap",
        flex: "1 0 auto",
        background: "#FFF",
        '&:hover' : {
          background: '#B3BBC3'
        }
      }
    },
  },
});
const useProgressStyle = makeStyles(() => ({
    root: {
        backgroundColor: (props) => props.isReference ? '#EAFCEE' : '#FBE4E0'
    },
    bar: {
        backgroundColor: (props) => props.isReference ? '#008A5D' : '#EC7963'
    }
}));
const useTextFieldStyle = makeStyles(() => ({
    root: {
        '& .MuiInputLabel-root.Mui-focused': {
            color: (props) => props.isReference ? '#008A5D' : '#EC7963'
        },
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
                border: (props) => props.isReference ? '2px solid #008A5D' : '2px solid #EC7963'
            }
        },
        '& .MuiOutlinedInput-input': {
            height: '15px',
        }
    }
}));

const CLOSE_ICON_SIZE = 18;
const useAlertStyles = makeStyles({
    root: {
        alignItems: 'center',
        '& .MuiIconButton-root': {
            width: 'auto',
            height: 'auto',
            padding: 4,
        },
        '& .MuiIconButton-root .MuiSvgIcon-root': {
            fontSize: `${CLOSE_ICON_SIZE}px`,
        },
    },
});

const AddNewAnnotation = (props) => {
    const { label, defaultValue, onKeyPressHandler, onConfirmHandler, onCancelHandler, disableEdit } = props;
    const textFieldClasses = useTextFieldStyle({isReference: window.reference});
    const textField = useRef(null);
    return (
        <div className={styles.addNewAnnotation}>
            <Icon name="AnnotationClass" />
            <div className={styles.inputArea}>
                <div>
                    <TextField
                        classes={{ root: textFieldClasses.root }}
                        label={label}
                        defaultValue={defaultValue}
                        size='small'
                        autoFocus={true}
                        style={{ width: '100%' }}
                        variant='outlined'
                        onKeyPress={onKeyPressHandler}
                        ref={textField}
                    />
                    <div className={styles.inputBtns}>
                        <Button variant='outlined'
                            onClick={() => {
                                onConfirmHandler(textField.current.querySelector('input').value.trim());
                            }}
                            disabled={disableEdit}
                        >確認</Button>
                        <Button variant='outlined' onClick={onCancelHandler}>取消</Button>
                    </div>
                </div>
                <span>{dateformat(Date.now(), 'yyyy-mm-dd HH:MM:ss')}</span>
            </div>
        </div>
    )
}

const AnnotationModal = ({ open, setOpen, selectBookID, goBook, setState, setShowMobileModal, bookName, waitingLogin, external }) => {

    const progressClasses = useProgressStyle({isReference: window.reference});
    const alertClasses = useAlertStyles();
    const [loadingOpen, setLoadingOpen] = useState(false);
    const { readAnnotationById } = useReadAnnotations();
    const { updatePreparationAnnotation, getPreparationList, syncPreparationForOffLine } = usePreparation();
    const { refreshReader } = useRefreshReader();
    const [{ role, token, isLogin }] = useStore(StoreTypes.user);
    const [{ bookId, books }] = useStore(StoreTypes.books);
    const [, loginDispatch] = useStore(StoreTypes.login);
    const [{ annotationId, name }, annotationDispatch] = useStore(StoreTypes.annotation);
    const { gaEvent } = useGAEvent();
    const [annotations, setAnnotations] = useState({});
    const [renew, setRenew] = useState(true);
    const [renameIndex, setRenameIndex] = useState(null);
    const [selectedAnnotation, setSelectedAnnotation] = useState('');
    const [openWarning, setOpenWaring] = useState({ open: false, type: '' });
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
    const syncPendingRef = useRef(false);
    const prevSigRef = useRef(null); // 上一次抓到的紀錄簽章,用來判斷同步後是否有變
    const [smoothBrush, setSmoothBrush] = useState(
        localStorage.getItem('smoothBrush') === 'true'
    );
    // 記錄彈窗開啟時的畫筆流暢模式狀態，供「確認進書」時判斷本次是否真的變更過
    const smoothBrushInitialRef = useRef(getSmoothBrushState());
    useEffect(() => {
        if (open) smoothBrushInitialRef.current = getSmoothBrushState();
    }, [open]);
    // isTeacherRole 而非 role === Roles.TEACHER：補習班教師需與教師齊頭（1661bd9b3）
    const annotationTypes = (!window.android && !window.ios && !window.electron && isTeacherRole(role)) ? AnnotationType.CLASS_PREPARATION : AnnotationType.GUEST;

    // 左側書籍資訊 block(封面 + 書名)
    const { BookContentRepository } = Repository;
    const coverBookId = bookId || selectBookID;
    const coverUrl = coverBookId ? BookContentRepository.getCoverUrls({ bookId: coverBookId }) : '';
    const storeBook = (books || []).find((b) => b.bookId === coverBookId);
    const displayBookName = bookName || (storeBook && (storeBook.displayName || storeBook.name)) || '';

    // 讀取/更新班級記錄筆數
    useEffect(() => {
        (async () => {
            if (!renew || !bookId) return;
            setLoadingOpen(true);
            if (waitingLogin) return;
            // annotationDispatch({type: types.RESET_ANNOTATION_INFO});
            const results = await getPreparationList(bookId, token, annotationTypes);
            results.sort((a, b) => {
                return b.updatedAt - a.updatedAt;
            })
            const sig = results
                .map((r) => `${r.id}:${r.updatedAt}:${r.isDeleted ? 1 : 0}:${r.name}`)
                .sort()
                .join('|');
            if (syncPendingRef.current) {
                syncPendingRef.current = false;
                if (prevSigRef.current !== null && sig === prevSigRef.current) {
                    setSnackbar({ open: true, severity: 'info', message: '已是最新紀錄' });
                } else {
                    setSnackbar({ open: true, severity: 'success', message: '同步成功' });
                }
            }
            prevSigRef.current = sig;
            let annotations = results.reduce((acc, v) => {
                if (!v.isDeleted) {
                    if (Array.isArray(acc[v.type])) {
                        acc[v.type].push(v);
                    } else {
                        acc[v.type] = [v];
                    }
                }
                return acc;
            }, {});
            setLoadingOpen(false);
            setAnnotations(annotations);
            if (annotations[annotationTypes] && annotations[annotationTypes].length > 0) {
                annotationDispatch({ type: types.UPDATE_ANNOTATION_INFO, annotationId: annotations[annotationTypes][0].id });
                setSelectedAnnotation(annotations[annotationTypes][0].id);
            } else {
                annotationDispatch({ type: types.RESET_ANNOTATION_INFO });
            }
            setRenew(false);
            EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: 0 } });
        })();
    }, [annotationTypes, bookId, getPreparationList, open, role, setLoadingOpen, token, renew, annotationDispatch, waitingLogin]);

    // 進書
    const { firebaseInitialize } = useFirebase();
    const fireEnterBookGA = () => {
        gaEvent({
            module: 'Bookshelf',
            tapEvent: '班級紀錄_確認進書',
            bookId: bookId || selectBookID || '',
            bookName: bookName,
            annotationId: annotationId || '',
        });

        // 畫筆流暢模式：開關本身會即時寫入 localStorage，事件依需求延到「確認進書」才送
        const currentSmoothBrush = getSmoothBrushState();
        if (currentSmoothBrush !== smoothBrushInitialRef.current) {
            gaEvent({
                module: 'Bookshelf',
                tapEvent: '畫筆流暢模式_開啟',
                smoothBrush: currentSmoothBrush,
                bookId: bookId || selectBookID || '',
                bookName: bookName,
                annotationId: annotationId || '',
            });
            smoothBrushInitialRef.current = currentSmoothBrush;
        }
    };
    const enterBook = async () => {
        setLoadingOpen(true);
        if (window.android || window.ios) {
            // console.group(`enter book: ${annotationId}`)
            if (annotationId && annotationId.length > 0) {
                const annotations = await readAnnotationById({ id: annotationId });
                refreshReader(annotations);
                fireEnterBookGA();
                setLoadingOpen(false);
                setShowMobileModal(false);
                return
            } else {
                EventBus.emit({
                    event: ReaderEvent.CreateAndEnterAnnotationEvent,
                    payload: {
                        bookId: bookId || selectBookID,
                        annotationType: annotationTypes,
                        annotationId: uuid(),
                        name: '未命名',
                        createNewAnnotation: true,
                        callback: async (info) => {
                            // console.groupCollapsed(`callback info: ${JSON.stringify(info)}`)
                            if (!info) {
                                setState(prev => {
                                    return {
                                        ...prev,
                                        open: true
                                    }
                                });
                            } else {
                                // firebaseInitialize();
                                // EventBus.emit({ event: ReaderToolsEvent.SetCourseInfoEvent, payload: { id: info.annotationId, userRole: role } });
                                const annotations = await readAnnotationById({ id: info.annotationId });
                                refreshReader(annotations);
                                fireEnterBookGA();
                                setLoadingOpen(false);
                                setShowMobileModal(false);
                            }
                        }
                    }
                });
                return
            }

        }
        // 只有網頁版在進書的時候會重新同步雲端資料
        if (!window.electron && !window.android && !window.ios) {
            await updatePreparationAnnotation({ id: annotationId, token })
            EventBus.emit({ event: ReaderEvent.SetProgressEvent, payload: { progress: 100 } })
        }
        // setProgressText("讀取完成")
        setTimeout(() => {
            setLoadingOpen(false);
            // setProgressOpen(false)
            EventBus.emit({
                event: ReaderEvent.CreateAndEnterAnnotationEvent,
                payload: {
                    bookId: bookId || selectBookID,
                    annotationType: annotationTypes,
                    annotationId: annotationId,
                    name: name.length === 0 ? '未命名' : name,
                    callback: (info) => {
                        if (!info) {
                            setState(prev => {
                                return {
                                    ...prev,
                                    open: true
                                }
                            });
                        } else {
                            firebaseInitialize();
                            fireEnterBookGA();
                            EventBus.emit({ event: ReaderToolsEvent.SetCourseInfoEvent, payload: { id: info.annotationId, userRole: role } })
                            goBook(bookId || selectBookID);
                        }
                    }
                }
            });
        }, 1000);
    }

    // 新增班級記錄
    const [createNewAnnotation, setCreateNewAnnotation] = useState(false);
    const [disableEdit, setDisableEdit] = useState(false);
    const createAnnotation = useCreateAnnotation();
    const { syncClassPreparation } = useSyncClassPreparationDispatcher();
    const openCreateNewAnnotation = () => {
        setCreateNewAnnotation(true);
        // annotationDispatch({type: types.RESET_ANNOTATION_INFO});
        setSelectedAnnotation('');
    }
    const addNewAnnotation = async (newAnnotationName) => {
        if (newAnnotationName !== '') {
            setDisableEdit(true);
            const annotationId = uuid();
            await createAnnotation({
                id: annotationId,
                bookId,
                name: newAnnotationName,
                type: annotationTypes,
                pageIndex: 0,
                annotations: [],
                isUpdate: false
            });
            if (annotationTypes === AnnotationType.CLASS_PREPARATION) {
                await syncClassPreparation(annotationId);
            }
            gaEvent({
                module: 'Bookshelf',
                tapEvent: '班級紀錄_新增',
                bookId: bookId || '',
                bookName: bookName,
            });
            setDisableEdit(false);
            setRenew(true);
        }
        setCreateNewAnnotation(false);
    }

    // 刪除班級記錄
    const findDeleteAnnotation = () => {
        return annotations[annotationTypes].filter(annotation => annotation.id === annotationId)[0];
    }

    // 複製班級記錄
    const [createCopyAnnotation, setCreateCopyAnnotation] = useState(false);
    const { copyPreparationAnnotation } = usePreparation();
    const [copyTextFieldValue, setCopyTextFieldValue] = useState('');
    const [copyAnnotationId, setCopyAnnotationId] = useState('');
    const openCopyAnnotationField = () => {
        const copiedAnnotation = annotations[annotationTypes].filter(annotation => annotation.id === annotationId)[0];
        const copiedAnnotationName = copiedAnnotation.name;
        setCopyAnnotationId(copiedAnnotation.id);
        // annotationDispatch({type: types.RESET_ANNOTATION_INFO});
        setSelectedAnnotation('');
        setCopyTextFieldValue(`${copiedAnnotationName}_複製`);
        setCreateCopyAnnotation(true);
    }
    const copyAnnotation = async (copyAnnotationName) => {
        const copiedAnnotation = annotations[annotationTypes].filter(annotation => annotation.id === copyAnnotationId)[0];
        const name = copyAnnotationName;
        if (name !== '') {
            setDisableEdit(true);
            await copyPreparationAnnotation({ annotation: { ...copiedAnnotation, name }, token });
            gaEvent({
                module: 'Bookshelf',
                tapEvent: '班級紀錄_複製紀錄',
                bookId: bookId || '',
                bookName: bookName || '',
                annotationId: annotationId || '',
            });
            setDisableEdit(false);
            setRenew(true);
        };
        setCreateCopyAnnotation(false);
    }

    // 重新命名
    const renameAnnotation = (name) => {
        if (name === '') return;
        EventBus.emit({
            event: ReaderEvent.SaveAnnotationNameEvent,
            payload: {
                annotationId: annotationId,
                name: name,
                callback: () => {
                    gaEvent({
                        module: 'Bookshelf',
                        tapEvent: '班級紀錄_重新命名',
                        bookId: bookId || '',
                        bookName: bookName || '',
                        annotationId: annotationId || '',
                    });
                }
            }
        });
        // setRenew(true);
    }

    //檢查 token 過期與否
    const checkTokenExpired = (token) => {
        const jwt = require('jsonwebtoken');
        const expiration = jwt.decode(token).exp;
        if (expiration < (Date.now() / 1000)) {
            setOpenWaring({
                open: true,
                type: 'token'
            });
        }
        return expiration < (Date.now() / 1000)
    }

    // 紀錄同步並檢查網路連線及 token
    const checkIsOnline = useOnlineCheck();
    const [{ progress }, readerDispatch] = useStore(StoreTypes.reader);
    const handleWebSyncAnnotation = async () => {
        const isOnline = await checkIsOnline();
        if (isOnline) {
            const token = JSON.parse(getNaniOneClassCookie('nani_oneclass_login_token')).jwt;
            const isTokenExpired = checkTokenExpired(token);
            if (isTokenExpired) return;
            gaEvent({
                module: 'Bookshelf',
                tapEvent: '班級紀錄_同步',
                bookId: bookId || '',
                bookName: bookName,
                annotationId: annotationId || '',
            });
            syncPendingRef.current = true;
            setRenew(true);
        } else {
            readerDispatch({
                type: types.SET_ONLINE_DIALOG,
                isOnLineDialog: true,
            });
        }
    }

    const handleOfflineSyncAnnotation = useCallback(async () => {
        const isOnline = await checkIsOnline();
        if (isOnline) {
          // 未登入時 cookie 與 localStorage 皆無 token，需回傳空值讓下方跳出登入視窗，而非 JSON.parse(null).jwt 拋錯
          const electronTokenInfo = window.electron ? JSON.parse(getNaniOneClassCookie('nani_oneclass_login_token')) : null;
          const token =  window.electron ? (electronTokenInfo && electronTokenInfo.jwt) : getNaniOneClassCookie('mobile_nani_oneclass_login_token');

          const needLogin = !isLogin && !token;
            if (!window.android && !window.ios && needLogin) {
                loginDispatch({ type: LOGIN_POPUP, popupState: true });
                return;
            }
            const isTokenExpired = checkTokenExpired(token);
            if (isTokenExpired) return;
            setLoadingOpen(true);
            // console.groupCollapsed(`handleOfflineSyncAnnotation: bookId-${JSON.stringify(bookId)} token-${JSON.stringify(token)} annotationTypes-${JSON.stringify(annotationTypes)}`);
            await syncPreparationForOffLine(bookId, token, annotationTypes);
            // console.groupCollapsed(`handleOfflineSyncAnnotation: results-${JSON.stringify(results)}`);
            gaEvent({
                module: 'Bookshelf',
                tapEvent: '班級紀錄_同步',
                bookId: bookId || '',
                bookName: bookName,
                annotationId: annotationId || '',
            });
            syncPendingRef.current = true;
            setRenew(true);
            // results.sort((a, b) => {
            //     return b.updatedAt - a.updatedAt;
            // })
            // let annotations = results.reduce((acc, v) => {
            //     if (!v.isDeleted) {
            //         if (Array.isArray(acc[v.type])) {
            //             acc[v.type].push(v);
            //         } else {
            //             acc[v.type] = [v];
            //         }
            //     }
            //     return acc;
            // }, {});
            // setLoadingOpen(false);
            // setAnnotations(annotations);
            // if (annotations[annotationTypes] && annotations[annotationTypes].length > 0) {
            //     annotationDispatch({type: types.UPDATE_ANNOTATION_INFO, annotationId: annotations[annotationTypes][0].id});
            //     setSelectedAnnotation(annotations[annotationTypes][0].id);
            // } else {
            //     annotationDispatch({type: types.RESET_ANNOTATION_INFO});
            // }
        } else {
            readerDispatch({
                type: types.SET_ONLINE_DIALOG,
                isOnLineDialog: true,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookId])

    // 刪除彈窗、token 過期彈窗功能
    const handleDeleteAnnotationCancel = () => setOpenWaring({ open: false, type: 'deleteAnnotation' });
    const handleDeleteAnnotationConfirm = () => {
        const deleteTarget = findDeleteAnnotation();
        EventBus.emit({
            event: ReaderEvent.DeleteAnnotationEvent,
            payload: {
                ...deleteTarget,
                callback: () => {
                    gaEvent({
                        module: 'Bookshelf',
                        tapEvent: '班級紀錄_刪除',
                        bookId: bookId || '',
                        bookName: bookName || '',
                        annotationId: annotationId || '',
                    });
                }
            }
        });
        setOpenWaring({ open: false, type: 'deleteAnnotation' });
        setRenew(true);
    }
    const handleTokenConfirm = () => {
        setOpenWaring({ open: false, type: 'token' })
    }
    const decideHandler = (type) => {
        switch (type) {
            case 'token':
                return {
                    handleCancel: null,
                    handleConfirm: handleTokenConfirm
                }
            case 'deleteAnnotation':
            default:
                return {
                    handleCancel: handleDeleteAnnotationCancel,
                    handleConfirm: handleDeleteAnnotationConfirm
                }
        }
    }


    return (
      <>
        <AnnotationWrapDialog
          open={open}
          onClose={(e, reason) => {
            if (reason === "escapeKeyDown") return;
            // App 是用 webview 開這個彈窗,「返回書櫃」等同關閉 webview
            if ((window.ios || window.android) && reason === "backButtonClick") {
              postToNative(NativeEvent.CloseWebview);
              return;
            }
            setOpen && setOpen(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          classes={useStyles()}
          BackdropProps={{
            style: {
              backgroundColor: "#FFF6EF",
              backgroundImage: "url('/img/annotationBackground.svg')",
              backgroundSize: "100%",
              backgroundPosition: "bottom",
              backgroundRepeat: "no-repeat",
            },
          }}
          className={styles.annotationModal}
          external={external}
        >
          <div className={styles.bodyRow}>
            <div className={styles.bookInfoBlock}>
              <div
                className={styles.bookCover}
                style={{ backgroundImage: `url(${coverUrl}),url(assets/img/cover.jpg)` }}
              />
              <div>
                <div className={styles.bookInfo}>您正在進入</div>
                <div className={styles.bookInfoName}>{displayBookName}</div>
              </div>
            </div>
            <div className={styles.mainColumn}>
              <DialogTitle>
                班級紀錄
              </DialogTitle>
              <DialogContent
                className={
                  !annotations[annotationTypes] ||
                  annotations[annotationTypes].length <= 0
                    ? "noData"
                    : null
                }
              >
                {createNewAnnotation && (
                  <AddNewAnnotation
                    label="班級紀錄名稱"
                    defaultValue="未命名"
                    onConfirmHandler={addNewAnnotation}
                    onCancelHandler={() => setCreateNewAnnotation(false)}
                    onKeyPressHandler={(e) => {
                      if (disableEdit) return;
                      if (e.key === "Escape") {
                        setCreateNewAnnotation(false);
                      }
                      if (e.key === "Enter") {
                        addNewAnnotation(e.target.value.trim());
                      }
                    }}
                    disableEdit={disableEdit}
                  />
                )}
                {createCopyAnnotation && (
                  <AddNewAnnotation
                    label="班級紀錄名稱"
                    defaultValue={copyTextFieldValue}
                    onConfirmHandler={copyAnnotation}
                    onCancelHandler={() => setCreateCopyAnnotation(false)}
                    onKeyPressHandler={(e) => {
                      if (disableEdit) return;
                      if (e.key === "Escape") {
                        setCreateCopyAnnotation(false);
                      }
                      if (e.key === "Enter") {
                        copyAnnotation(e.target.value.trim());
                      }
                    }}
                    disableEdit={disableEdit}
                  />
                )}
                {loadingOpen && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={progress || 0}
                      classes={{
                        root: progressClasses.root,
                        bar: progressClasses.bar,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        bottom: "35%",
                        transform: "translateX(-50%)",
                        textAlign: "center",
                        fontSize: "0.9rem",
                      }}
                    >
                      資料讀取中...
                    </div>
                    {window.reference ? (
                      <LottiePlayerReference width="50%" />
                    ) : (
                      <LottiePlayer width="50%" />
                    )}
                  </>
                )}

                {!loadingOpen &&
                  !createNewAnnotation &&
                  !createCopyAnnotation &&
                  (!annotations[annotationTypes] ||
                    annotations[annotationTypes].length <= 0) && (
                    <div className={styles.noAnnotation}>
                      無班級紀錄
                    </div>
                  )}
                {!loadingOpen && (
                  <EnhancedTable
                    listData={annotations[annotationTypes] || []}
                    setOpenWaring={setOpenWaring}
                    openCopyAnnotationField={openCopyAnnotationField}
                    renameAnnotation={renameAnnotation}
                    setShowMobileModal={setShowMobileModal}
                    refreshReader={refreshReader}
                    readAnnotationById={readAnnotationById}
                    createCopyAnnotation={createCopyAnnotation}
                    createNewAnnotation={createNewAnnotation}
                    renameIndex={renameIndex}
                    setRenameIndex={setRenameIndex}
                    selectedAnnotation={selectedAnnotation}
                    setSelectedAnnotation={setSelectedAnnotation}
                  />
                )}
              </DialogContent>
              {!window.electron && !window.android && !window.ios && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 32px 0",
                }}>
                  <SwitchButton
                    value={smoothBrush}
                    activeColor={window.reference ? "#00A15D" : "#121232"}
                    onChange={(val) => {
                      localStorage.setItem('smoothBrush', val);
                      setSmoothBrush(val);
                    }}
                  />
                  <span style={{ fontSize: "14px", color: "#454B5C" }}>畫筆流暢模式</span>
                </div>
              )}
              <DialogActions>
                <span className={styles.annotationCount}>
                  目前有
                  {annotations[annotationTypes]
                    ? annotations[annotationTypes].length
                    : 0}
                  筆
                </span>
                <div className={styles.actions}>
                  <Button
                    size="large"
                    variant="outlined"
                    className='actionSyncBtn'
                    onClick={
                      window.electron || window.android || window.ios
                        ? handleOfflineSyncAnnotation
                        : handleWebSyncAnnotation
                    }
                    disabled={
                      loadingOpen ||
                      createCopyAnnotation ||
                      createNewAnnotation ||
                      renameIndex !== null
                    }
                  >
                    紀錄同步
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    className='actionAddBtn'
                    onClick={openCreateNewAnnotation}
                    disabled={
                      loadingOpen ||
                      createCopyAnnotation ||
                      createNewAnnotation ||
                      renameIndex !== null
                    }
                  >
                    ＋ 新增班級紀錄
                  </Button>
                  <Button
                    className={styles.confirm}
                    size="large"
                    variant="outlined"
                    style={
                      loadingOpen ||
                      createNewAnnotation ||
                      createCopyAnnotation ||
                      renameIndex !== null
                        ? {
                            background: "none",
                            color: "rgba(0, 0, 0, 0.26)",
                            border: "1px solid rgba(0, 0, 0, 0.12)",
                            whiteSpace: "nowrap",
                            flex: "1 0 auto",
                          }
                        : {
                            background: window.reference ? "#00A15D" : "#121232",
                            color: "white",
                            whiteSpace: "nowrap",
                            flex: "1 0 auto",
                            ...(window.reference && { border: "none" }),
                          }
                    }
                    onClick={enterBook}
                    disabled={
                      loadingOpen ||
                      createNewAnnotation ||
                      createCopyAnnotation ||
                      renameIndex !== null
                    }
                  >
                    確認
                  </Button>
                </div>
              </DialogActions>
            </div>
          </div>
        </AnnotationWrapDialog>
        <WarningDialog
          open={openWarning.open}
          type={openWarning.type}
          {...decideHandler(openWarning.type)}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={(e, reason) => {
            if (reason === "clickaway") return;
            setSnackbar((s) => ({ ...s, open: false }));
          }}
        >
          <MuiAlert
            className={alertClasses.root}
            variant="filled"
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </>
    );
};
export default AnnotationModal;
